export interface FamilyMemberAgeInput {
  age?: number | string | null;
  dob?: Date | string | null;
  dateOfBirth?: Date | string | null;
}

export function getFamilyMemberAgeDisplay(member: FamilyMemberAgeInput): string | null {
  const dobRaw = member.dob ?? member.dateOfBirth;
  return formatDependentAgeFromBirthDate(dobRaw) ?? formatFamilyMemberStoredAge(member.age);
}

function formatDependentAgeFromBirthDate(dob: Date | string | null | undefined): string | null {
  if (!dob || dob === '--') {
    return null;
  }

  const birthDate = typeof dob === 'string' ? new Date(dob) : dob;
  if (isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();
  if (birthDate > today) {
    return null;
  }

  const age = getAge(birthDate, today);
  if (age >= 1) {
    return String(age);
  }

  let months = (today.getFullYear() - birthDate.getFullYear()) * 12 + (today.getMonth() - birthDate.getMonth());
  if (today.getDate() < birthDate.getDate()) {
    months--;
  }
  if (months >= 1) {
    return months === 1 ? '1 month' : `${months} months`;
  }

  const totalDays = Math.floor((today.getTime() - birthDate.getTime()) / (24 * 60 * 60 * 1000));
  const weeks = Math.floor(totalDays / 7);
  const daysAfterWeeks = totalDays % 7;
  if (weeks >= 1) {
    const weekPart = weeks === 1 ? '1 week' : `${weeks} weeks`;
    if (daysAfterWeeks > 0) {
      const dayPart = daysAfterWeeks === 1 ? '1 day' : `${daysAfterWeeks} days`;
      return `${weekPart} and ${dayPart}`;
    }
    return weekPart;
  }

  if (totalDays >= 1) {
    return totalDays === 1 ? '1 day' : `${totalDays} days`;
  }

  return '0 days';
}

function formatFamilyMemberStoredAge(age: number | string | null | undefined): string | null {
  if (age == null || age === '') {
    return null;
  }

  const numericAge = Number(age);
  if (Number.isNaN(numericAge)) {
    return typeof age === 'string' ? age : null;
  }

  return numericAge >= 1 ? String(numericAge) : null;
}

function getAge(birthDate: Date, today: Date): number {
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}
